import { useGetMaskId } from 'components/hooks/maskId/useGetMaskId';
import MessageRenderer from 'components/MessageRenderer';
import ClickOutside from 'components/OnClickOutside';
import ResizeAndDragabbleNotes from 'components/ResizeAndDragabbleNotes';
import { lighten } from 'polished';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAllBook } from 'store/books/selectors';
import { useResources, useVideos } from 'store/resources/selectors';
import { useStory } from 'store/story/selectors';
import styled from 'styled-components';
import { COLOR } from 'utils/constants/color';
import { useFullScreen } from 'utils/context/FullScreenProvider';
import { imageFetchFromPodcast, isVideo } from 'utils/lib/ebookHelper';

const PreviewNote = ({ data, open, setOpen, onOpenNotes, seeAllNotes, action }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [disableDragOnFocus, setDisableDragOnFocus] = useState(false);
  const [position, setPosition] = useState({
    x: -450,
    y: 10,
    width: 215,
    height: 255,
  });
  const { currentFullScreen } = useFullScreen();
  const booksAllList = useAllBook();
  const video = useVideos();
  const storyList = useStory();
  const { maskId } = useGetMaskId(data?.from, data?.page, data?.chapter);

  const findStoryDetails = useCallback((pageNumber) => {
    const pageNum = parseInt(pageNumber, 10);
    const storyPage = storyList.find((p) => {
      if (!Array.isArray(p.levels)) return false;
      return p.levels.some((level) => {
        const startNum = parseInt(level.start, 10);
        const finishNum = parseInt(level.finish, 10);
        if (isNaN(startNum) || isNaN(finishNum)) return false;
        return startNum <= pageNum && pageNum <= finishNum;
      });
    });
    if (!storyPage) return null;
    const matchingLevel = storyPage.levels.find((level) => {
      const startNum = parseInt(level.start, 10);
      const finishNum = parseInt(level.finish, 10);
      if (isNaN(startNum) || isNaN(finishNum)) return false;
      return startNum <= pageNum && pageNum <= finishNum;
    });

    if (!matchingLevel) return null;
    const startNum = parseInt(matchingLevel.start, 10);
    const relativePage = pageNum - startNum + 1;
    const title = storyPage?.title || "";
    const levelName = matchingLevel?.level || "";
    return { relativePage, title, levelName, pageNum };
  }, [storyList]);

  const getTitleAndLevel = useCallback(
    (pageNumber) => {
      const details = findStoryDetails(pageNumber);
      if (details) return `${details.title} ${details.levelName}`;
      return `${pageNumber}`;
    },
    [findStoryDetails],
  );

  const getStoryRelativePageOnly = useCallback(
    (pageNumber) => {
      const details = findStoryDetails(pageNumber);
      if (details) return `${details.relativePage}`;
      return `${pageNumber}`;
    },
    [storyList],
  );

  const getType = useCallback((bookId) => {
    if (!bookId) return null;
    const findBook = booksAllList.find((obj) => bookId == obj?.id);
    return findBook?.type;
  }, [booksAllList]);

  const handleResize = () => {
    if (currentFullScreen) {
      setPosition((prev) => ({ ...prev, x: -660, y: 50 }));
    } else {
      if (window.innerWidth <= 400) {
        setPosition((prev) => ({ ...prev, y: -250, x: -260 }));
      } else if (window.innerWidth <= 768) {
        setPosition((prev) => ({
          ...prev,
          y: currentFullScreen ? -20 : -250,
          x: currentFullScreen ? -300 : -240,
        }));
      } else if (window.innerWidth <= 1024) {
        setPosition((prev) => ({ ...prev, y: currentFullScreen ? -20 : -200, x: -400 }));
      } else {
        setPosition((prev) => ({ ...prev, x: -600 }));
      }
    }
  };
  
  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentFullScreen]);

  const handleClose = () => {
    setIsDragging(false);
    setOpen({ open: false, data: null });
  };

  const messageNote = useMemo(() => {
    return data?.content?.content || data?.content || '';
  }, [data]);

  const noteColor = useMemo(() => {
    if (!data?.color) return null;
    return data?.color?.color ? data?.color?.color : data?.color;
  }, [data?.color]);

  const resources = useResources();

  const bookName = useMemo(() => {
    if (data?.chapter && data?.page) {
      const findBook = booksAllList.find((obj) => data?.chapter == obj?.id);
      const findPage = findBook?.pages.find((obj) => data?.page == obj?.pageId);

      if (!findBook) {
        const findVideo = video.find((obj) => data?.chapter == obj?.id);

        return {
          name: findVideo?.displayName || 'N/A',
          image: isVideo(data?.chapter, resources)
            ? imageFetchFromPodcast(data?.chapter, resources)
            : findVideo?.thumbnail,
        };
      }

      return {
        name: getTitleAndLevel(data?.page),
        image: findPage?.resource,
      };
    }
    return {};
  }, [data?.chapter, data?.page, getTitleAndLevel, resources, booksAllList, video]);

  const hasNotesOnPage = useMemo(() => {
    if (!data?.chapter || !data?.page || !seeAllNotes) return false;
    const currentPageNotes = Array.isArray(seeAllNotes) 
      ? seeAllNotes.filter(k => k.chapter == data.chapter && k.page == data.page) 
      : [];
    return currentPageNotes.length > 0;
  }, [seeAllNotes, data?.chapter, data?.page]);

  if (!open) return null;
  return (
    <ClickOutside onClickOutside={() => setIsDragging(false)}>
      <ResizeAndDragabbleNotes
        onFocusValue={isDragging || disableDragOnFocus}
        // className="bubbleNotesTopSection"
        defaultValue={position}
        minWidth="var(--min-width)"
        minHeight="var(--min-height)"
        bounds="window"
        enableResizing={false}
        from="notes"
        style={{
          boxShadow: '0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23)',
          backgroundColor: COLOR.white,
          overflow: 'hidden',
        }}
        className={`bubbleNotesTopSection notes-widget ${isDragging ? 'NotesMobAndTab' : ''} 
          `}
      >
        <div
          onTouchStart={(event) => {
            event.stopPropagation();
            setIsDragging(true);
          }}
          className="bubbleNotesHghtSet"
        >
          {open && (
            <>
              {
                <>
                  <div className="notesCard">
                    <div className="notesCardLeft">
                      <Image src={bookName?.image} alt="" />
                    </div>

                    <div className="notesCardRght">
                      <p>{`Titulo:  ${data?.title || bookName?.name}`}</p>

                      <label>
                        {data?.from !== 'videosection' && (
                          <span>
                            {`Page. ${
                              getType(data?.chapter) === 'story'
                                ? getStoryRelativePageOnly(data?.page)
                                : data?.newMaskNote || maskId || data?.page || ''
                            }`}
                          </span>
                        )}
                      </label>
                      {hasNotesOnPage && action !== 'shareByMe' && (
                        <button
                              onClick={() => {
                                if (onOpenNotes) {
                                  onOpenNotes();
                                }
                                setOpen({ open: false, data: null });
                              }}
                              onTouchStart={() => {
                                if (onOpenNotes) {
                                  onOpenNotes();
                                }
                                setOpen({ open: false, data: null });
                              }}
                               className="saved-notes-label">
                              SEE MY NOTES
                        </button>
                      )}
                    </div>
                  </div>

                  <NoteSec color={noteColor} className="notesCardMsg">
                    <NotesText>Notes:</NotesText>
                    <MessageRenderer style={{ color: 'rgb(0 0 0)' }} data={messageNote} />
                  </NoteSec>
                </>
              }
            </>
          )}

          <Footer>
            <button className="notesSaveCloseBtn notesSaveCloseBtnNw" onClick={handleClose}>
              Close
            </button>
          </Footer>
          <div className="widgetFooter" style={{backgroundColor: '#010a52'}}><label>Widget</label></div>
        </div>
      </ResizeAndDragabbleNotes>
    </ClickOutside>
  );
};

export default PreviewNote;

const SearchArea = styled.div`
  border-radius: 20px;
  height: 38px;
  width: 100%;
  display: flex;
  flex-direction: row;
  background: #ddd;
  justify-content: center;
  align-items: center;
`;

const SearchIconArea = styled.div`
  width: 45px;
  height: 38px;
  border-radius: 50% 0% 0% 50%;
  background: #efefef;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 90%;
  height: 30px;
  font-size: 15px;
  background: transparent;
  border: none;
  outline: none;
  margin-left: 5px;
  color: black;
  ::placeholder,
  ::-webkit-input-placeholder {
    color: ${COLOR.black500};
  }
`;

const ButtonClear = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
`;

const Image = styled.img`
  background-color: ${COLOR.white};
  box-shadow:
    0 10px 20px rgba(0, 0, 0, 0.19),
    0 6px 6px rgba(0, 0, 0, 0.23);
  height: auto;
  width: 33.87%;
  display: flex;
  margin: 0 auto;
  margin-top: 22px;
`;

const NotesText = styled.span`
  color: #2e2e2e;
  display: inline-block;
  font-size: 16px;
  font-weight: 600;
  line-height: 16px;
  margin-right: 5px;
  margin-top: 3px;
`;

const NoteSec = styled.div`
  background-color: ${(p) => {
    if (!p.color) return 'white';
    const colorValue = typeof p.color === 'string' ? p.color.trim() : '';
    if (!colorValue || (!colorValue.startsWith('#') && !colorValue.startsWith('rgb') && !colorValue.startsWith('hsl'))) {
      return 'white';
    }
    try {
      return lighten(0.4, colorValue);
    } catch (error) {
      return 'white';
    }
  }};
  display: flex;
  flex-direction: row;
  margin-bottom: 16px;
  width: 100%;
  margin-top: 20px;
  height: 127px;
  padding: 5px;
  overflow: auto;
`;

const Footer = styled.div`
  position: absolute;
  bottom: 15px;
  left: 0;
  width: 100%;
  background-color: #010a52;
  height: 75px;
  border-radius: 0%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NotificationDot = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 10px;
  height: 10px;
  background-color: #ff0000;
  border-radius: 50%;
  border: 2px solid #ffffff;
  z-index: 10;
`;
